import axios from 'axios'

import { getApiGatewayUrl } from '@/config/runtime'
import { toServiceError } from '@/services/http/client'
import type {
  ApplicabilityState,
  ReturnCaseWorkspace,
  SalaryComponentInput,
  SalaryEmployerInput,
  CommonLedgerReadModel,
  CommonLedgerResourceResponse,
  SalaryEmploymentInput,
  SalaryHraInput,
  SalaryLtaInput,
  SalaryModuleReadModel,
  SalaryReliefInput,
  SalaryResourceResponse,
  CollaborationMutation,
  CollaborationWorkspace,
  AttestationMutation,
  SectionAttestationState,
  BrokerImportRowPage,
  CapitalResourceResponse,
  CapitalWorkspace,
  BusinessResourceResponse,
  BusinessWorkspace,
  InternationalResourceResponse,
  InternationalWorkspace,
  FinalDeclarationSummary,
  ReviewWorkspace,
} from '@/types/returnCase.types'

const client = axios.create({
  baseURL: getApiGatewayUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

export interface CaseAccessCompletion {
  access_token: string
  token_type: 'bearer'
  case_id: string
  grant_id: string
  expires_at: string
  participant_kind: 'PRINCIPAL' | 'REPRESENTATIVE'
  allowed_actions: string[]
  identity_verified_at?: string
}

async function safe<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation()
  } catch (error) {
    throw toServiceError(error)
  }
}

export const CASE_ACCESS_SESSION_KEY = 'taxhive.case_access.v1'
const bearer = (completion: CaseAccessCompletion, idempotent = false) => ({
  headers: {
    Authorization: `Bearer ${completion.access_token}`,
    ...(idempotent ? { 'Idempotency-Key': crypto.randomUUID() } : {}),
  },
})

export const caseAccessHttp = {
  begin: (token: string, email: string, purpose: string) =>
    safe(
      async () =>
        (
          await client.post<{ redemption_id: string; message: string }>(
            '/case-invite-redemptions:begin',
            { token, email, purpose }
          )
        ).data
    ),
  verifyOtp: (redemptionId: string, otp: string) =>
    safe(
      async () =>
        (
          await client.post<{ status: 'PENDING' | 'VERIFIED'; message: string }>(
            '/case-invite-redemptions:verify-otp',
            { redemption_id: redemptionId, otp }
          )
        ).data
    ),
  complete: (redemptionId: string) =>
    safe(
      async () =>
        (
          await client.post<CaseAccessCompletion>('/case-invite-redemptions:complete', {
            redemption_id: redemptionId,
          })
        ).data
    ),
  store: (completion: CaseAccessCompletion) =>
    sessionStorage.setItem(CASE_ACCESS_SESSION_KEY, JSON.stringify(completion)),
  load: (): CaseAccessCompletion | null => {
    try {
      const raw = sessionStorage.getItem(CASE_ACCESS_SESSION_KEY)
      return raw ? (JSON.parse(raw) as CaseAccessCompletion) : null
    } catch {
      return null
    }
  },
  beginStepUp: (completion: CaseAccessCompletion, email: string) =>
    safe(
      async () =>
        (
          await client.post<{ challenge_id: string; message: string }>(
            '/case-access-sessions:begin-step-up',
            { email },
            bearer(completion)
          )
        ).data
    ),
  verifyStepUp: (completion: CaseAccessCompletion, challengeId: string, otp: string) =>
    safe(
      async () =>
        (
          await client.post<CaseAccessCompletion>(
            '/case-access-sessions:verify-step-up',
            { challenge_id: challengeId, otp },
            bearer(completion)
          )
        ).data
    ),
  workspace: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<ReturnCaseWorkspace>(
            `/return-preparation-cases/${completion.case_id}/workspace`,
            {
              headers: { Authorization: `Bearer ${completion.access_token}` },
            }
          )
        ).data
    ),
  applicability: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<ApplicabilityState>(
            `/return-preparation-cases/${completion.case_id}/applicability`,
            {
              headers: { Authorization: `Bearer ${completion.access_token}` },
            }
          )
        ).data
    ),
  salaryModule: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<SalaryModuleReadModel>(
            `/return-preparation-cases/${completion.case_id}/modules/salary`,
            bearer(completion)
          )
        ).data
    ),
  createSalaryEmployer: (completion: CaseAccessCompletion, input: SalaryEmployerInput) =>
    safe(
      async () =>
        (
          await client.post<SalaryResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/salary/employers`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  createSalaryEmployment: (completion: CaseAccessCompletion, input: SalaryEmploymentInput) =>
    safe(
      async () =>
        (
          await client.post<SalaryResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/salary/employments`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  createSalaryComponent: (completion: CaseAccessCompletion, input: SalaryComponentInput) =>
    safe(
      async () =>
        (
          await client.post<SalaryResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/salary/components`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  createSalaryHraPeriod: (completion: CaseAccessCompletion, input: SalaryHraInput) =>
    safe(
      async () =>
        (
          await client.post<SalaryResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/salary/hra-periods`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  createSalaryLtaJourney: (completion: CaseAccessCompletion, input: SalaryLtaInput) =>
    safe(
      async () =>
        (
          await client.post<SalaryResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/salary/lta-journeys`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  createSalaryReliefItem: (completion: CaseAccessCompletion, input: SalaryReliefInput) =>
    safe(
      async () =>
        (
          await client.post<SalaryResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/salary/relief-items`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  commonLedger: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<CommonLedgerReadModel>(
            `/return-preparation-cases/${completion.case_id}/modules/common-ledger`,
            bearer(completion)
          )
        ).data
    ),
  capitalWorkspace: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<CapitalWorkspace>(
            `/return-preparation-cases/${completion.case_id}/modules/capital-gains`,
            bearer(completion)
          )
        ).data
    ),
  brokerImportRows: (completion: CaseAccessCompletion, batchId: string, page = 1, pageSize = 100) =>
    safe(
      async () =>
        (
          await client.get<BrokerImportRowPage>(
            `/return-preparation-cases/${completion.case_id}/modules/capital-gains/broker-imports/${batchId}/rows`,
            { ...bearer(completion), params: { page, page_size: pageSize } }
          )
        ).data
    ),
  flagBrokerImportRow: (
    completion: CaseAccessCompletion,
    rowId: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<CapitalResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/capital-gains/broker-import-rows/${rowId}/flags`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  businessWorkspace: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<BusinessWorkspace>(
            `/return-preparation-cases/${completion.case_id}/modules/business`,
            bearer(completion)
          )
        ).data
    ),
  createBusinessResource: (
    completion: CaseAccessCompletion,
    path: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<BusinessResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/business/${path}`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  evaluateBusiness: (completion: CaseAccessCompletion, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await client.post<BusinessResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/business:evaluate`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  internationalWorkspace: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<InternationalWorkspace>(
            `/return-preparation-cases/${completion.case_id}/modules/international`,
            bearer(completion)
          )
        ).data
    ),
  createInternationalResource: (
    completion: CaseAccessCompletion,
    path: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<InternationalResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/international/${path}`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  evaluateInternational: (completion: CaseAccessCompletion, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await client.post<InternationalResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/international:evaluate`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  createCommonLedgerResource: (
    completion: CaseAccessCompletion,
    path: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<CommonLedgerResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/${path}`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  disposeContinuityItem: (
    completion: CaseAccessCompletion,
    itemId: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<CommonLedgerResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/continuity/items/${itemId}:dispose`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  updateCommonLedgerEvidence: (
    completion: CaseAccessCompletion,
    requirementKey: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.put<CommonLedgerResourceResponse>(
            `/return-preparation-cases/${completion.case_id}/modules/common-ledger/evidence/${requirementKey}`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  collaboration: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<CollaborationWorkspace>(
            `/return-preparation-cases/${completion.case_id}/collaboration`,
            bearer(completion)
          )
        ).data
    ),
  respondToQuery: (
    completion: CaseAccessCompletion,
    queryId: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<CollaborationMutation>(
            `/return-preparation-cases/${completion.case_id}/queries/${queryId}:respond`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  attestation: (completion: CaseAccessCompletion, sectionKey: string) =>
    safe(
      async () =>
        (
          await client.get<SectionAttestationState>(
            `/return-preparation-cases/${completion.case_id}/sections/${sectionKey}/attestation-state`,
            bearer(completion)
          )
        ).data
    ),
  confirmSection: (
    completion: CaseAccessCompletion,
    sectionKey: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<AttestationMutation>(
            `/return-preparation-cases/${completion.case_id}/sections/${sectionKey}/confirmations`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  confirmHighRiskItem: (
    completion: CaseAccessCompletion,
    sectionKey: string,
    itemKey: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await client.post<AttestationMutation>(
            `/return-preparation-cases/${completion.case_id}/sections/${sectionKey}/high-risk-items/${encodeURIComponent(itemKey)}/confirmations`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
  reviewWorkspace: (completion: CaseAccessCompletion) =>
    safe(
      async () =>
        (
          await client.get<ReviewWorkspace>(
            `/return-preparation-cases/${completion.case_id}/review-workspace`,
            bearer(completion)
          )
        ).data
    ),
  signFinalDeclaration: (
    completion: CaseAccessCompletion,
    snapshotId: string,
    input: {
      expected_revision: number
      displayed_snapshot_hash: string
      displayed_summary_hash: string
      wording_version: '1.0.0'
      accepted: true
    }
  ) =>
    safe(
      async () =>
        (
          await client.post<FinalDeclarationSummary>(
            `/return-preparation-cases/${completion.case_id}/review-snapshots/${snapshotId}/final-declarations`,
            input,
            bearer(completion, true)
          )
        ).data
    ),
}
